import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import RcTiptapEditor from 'reactjs-tiptap-editor';
import {
  BaseKit,
  Blockquote,
  Bold,
  BulletList,
  Clear,
  Code,
  CodeBlock,
  Color,
  ColumnActionButton,
  Emoji,
  ExportPdf,
  ExportWord,
  FontFamily,
  FontSize,
  Heading,
  Highlight,
  History,
  HorizontalRule,
  Image,
  ImportWord,
  Indent,
  Italic,
  LineHeight,
  Link,
  Mention,
  MoreMark,
  OrderedList,
  SearchAndReplace,
  SlashCommand,
  Strike,
  Table,
  TaskList,
  TextAlign,
  TextDirection,
  Underline
} from 'reactjs-tiptap-editor/extension-bundle';
import 'reactjs-tiptap-editor/style.css';
import { VariableSpan } from './VariableSpan';
import './editor.scss';
import { ImageWithUpload } from './ImageWithUpload';

const getExtensions = () => [
  BaseKit.configure({
    multiColumn: true,
    placeholder: { showOnlyCurrent: true },
    characterCount: false
  }),
  VariableSpan,
  History,
  SearchAndReplace,
  TextDirection,
  Clear,
  FontFamily,
  Heading.configure({ spacer: true }),
  FontSize,
  Bold,
  Italic,
  Underline,
  Strike,
  MoreMark,
  Emoji,
  Color.configure({ spacer: true }),
  Highlight,
  BulletList,
  OrderedList,
  TextAlign.configure({ types: ['heading', 'paragraph'], spacer: true }),
  Indent,
  LineHeight,
  TaskList.configure({ spacer: true, taskItem: { nested: true } }),
  Link,
  ImageWithUpload,
  Blockquote.configure({ spacer: true }),
  SlashCommand,
  HorizontalRule,
  Code.configure({ toolbar: false }),
  CodeBlock.configure({ defaultTheme: 'dracula' }),
  ColumnActionButton,
  Table,
  ExportPdf.configure({ spacer: true }),
  ImportWord.configure({
    upload: files =>
      Promise.resolve(
        files.map(file => ({
          src: URL.createObjectURL(file),
          alt: file.name
        }))
      )
  }),
  ExportWord,
  Mention
];

function Editor({ content, onContentChange, readOnly = false }) {
  const refEditor = useRef(null);

  const onValueChange = useCallback(value => onContentChange(value), [onContentChange]);

  const extensions = useMemo(() => getExtensions(), []);

  useEffect(() => {
    if (refEditor.current?.editor?.getHTML() !== content) {
      refEditor.current?.editor?.commands.setContent(content);
      onContentChange(content);
    }
  }, [content]);

  return (
    <RcTiptapEditor
      ref={refEditor}
      output="html"
      content={content}
      onChangeContent={onValueChange}
      extensions={extensions}
      dark={false}
      editable={true}
      disabled={readOnly}
    />
  );
}

export default Editor;
